import React from 'react';

import { messageFromError } from '@oysterjs/core/graphql/errors';
import { client } from '~/api/client';
import {
  BuildHtmlDocumentRenderUrlDocument,
  DocumentRefitOutcome,
  type BuildHtmlDocumentRenderUrlInput,
  type BuildHtmlDocumentRenderUrlMutation,
  type BuildHtmlDocumentRenderUrlMutationVariables
} from '~/api/graphql';

import {
  type AttachmentGroupMember,
  groupMergeAttachments,
  groupRenderPlan,
  isPdfOnlyGroup
} from '~/components/workspaces/lib/attachment-group';

/** The merged PDF as stored, which is the shape the composer attaches by. */
export type MergedGroupFile = { id: string; name: string; size: number };

/**
 * Combines a sequence of documents into one stored PDF.
 *
 * Nothing on the server merges and stores in one call: `buildHtmlDocumentRenderUrl`
 * hands back a URL that the statics service renders and concatenates on GET, and
 * the bytes are streamed rather than kept. So the merge is completed here — fetch
 * the render, then store what came back through the same upload an attached file
 * uses. That is why a combined PDF is a workspace file and not a reference: the
 * only durable form the merged bytes have is one this browser put there.
 *
 * Consequently the merge is a snapshot. A document attached on its own is
 * re-rendered when the email is sent; a document combined into a PDF is the pages
 * it had at the moment of combining, and the builder says so.
 */
export function useAttachmentGroupMerge(props: {
  workspaceId: string;
  /** Latest version of an artifact in this workspace, or undefined when it is gone. */
  latestVersionOf: (artifactId: string) => number | undefined;
  /** Puts the merged bytes in the workspace; resolves null when nothing was stored. */
  storeFile: (file: File) => Promise<MergedGroupFile | null>;
}): (
  members: AttachmentGroupMember[],
  name: string,
  anchorId: string,
  /** Producer signature stamped onto the document the PDF is built around. */
  producerInformationId: string | null
) => Promise<MergedGroupFile> {
  const { workspaceId, latestVersionOf, storeFile } = props;

  // The composer rebuilds these on every render, and a merge in flight must keep
  // working from the ones it started with.
  const latestVersionOfRef = React.useRef(latestVersionOf);
  const storeFileRef = React.useRef(storeFile);
  React.useEffect(() => {
    latestVersionOfRef.current = latestVersionOf;
    storeFileRef.current = storeFile;
  });

  return React.useCallback(
    async (
      members: AttachmentGroupMember[],
      name: string,
      anchorId: string,
      producerInformationId: string | null
    ): Promise<MergedGroupFile> => {
      // PDFs alone have nothing to render, so the render is asked for without an
      // artifact and joins them in the order the sender ticked them.
      let input: BuildHtmlDocumentRenderUrlInput;
      if (isPdfOnlyGroup(members)) {
        input = {
          workspaceId,
          prefixAttachments: groupMergeAttachments(members),
          suffixAttachments: []
        };
      } else {
        const plan = groupRenderPlan(members, anchorId);
        if (!plan) {
          throw new Error('A combined PDF has to include at least one workspace document.');
        }
        if (latestVersionOfRef.current(plan.primaryArtifactId) === undefined) {
          throw new Error('One of these documents is no longer in this workspace.');
        }
        input = {
          workspaceId,
          artifactId: plan.primaryArtifactId,
          // Without a version the server renders the latest one, which a list read here could
          // trail. A client gets it as its Design lays it out; documents attached around it
          // render as saved.
          refitDesign: true,
          // The sender arranged this order themselves; a template's own
          // cover pages would insert documents they never asked for.
          skipTemplateFiles: true,
          producerInformationId,
          prefixAttachments: plan.prefixAttachments,
          suffixAttachments: plan.suffixAttachments
        };
      }

      let url: string | undefined;
      let outcome: DocumentRefitOutcome | null | undefined;
      try {
        const result = await client.mutate<
          BuildHtmlDocumentRenderUrlMutation,
          BuildHtmlDocumentRenderUrlMutationVariables
        >({
          mutation: BuildHtmlDocumentRenderUrlDocument,
          variables: { input }
        });
        url = result.data?.buildHtmlDocumentRenderUrl?.url;
        outcome = result.data?.buildHtmlDocumentRenderUrl?.outcome;
      } catch (err) {
        throw new Error(messageFromError(err, 'We could not combine these into one PDF.'));
      }
      // The stored PDF is what the client receives, so one in the previous layout is never kept.
      const refusal = layoutRefusal(outcome);
      if (refusal) throw new Error(refusal);
      if (!url) {
        throw new Error('We could not combine these into one PDF.');
      }

      const response = await fetch(url).catch(() => null);
      if (!response?.ok) {
        throw new Error('We could not combine these into one PDF. Try again.');
      }
      const merged = new File([await response.blob()], name, { type: 'application/pdf' });
      if (merged.size === 0) {
        throw new Error('The combined PDF came back empty, so nothing was attached.');
      }

      const stored = await storeFileRef.current(merged);
      if (!stored) {
        throw new Error(`We could not store “${name}”.`);
      }
      return stored;
    },
    [workspaceId]
  );
}

const layoutRefusal = (outcome: DocumentRefitOutcome | null | undefined): string | null => {
  switch (outcome) {
    case DocumentRefitOutcome.RefitPending:
      return 'The document is still updating to its Design. Combine again in a moment.';
    case DocumentRefitOutcome.RefitFailed:
      return "The document couldn't be fitted to its Design, so nothing was combined. Try again later, or ask someone who can edit it to fit it to its Design.";
    case DocumentRefitOutcome.Changed:
      return 'The document changed while it was updating to its Design. Combine again.';
    default:
      return null;
  }
};
